import {
    alpha,
    Box,
    keyframes,
    LinearProgress,
    linearProgressClasses,
    LinearProgressProps,
    SxProps,
    Theme,
    Typography,
} from '@mui/material';

interface TagProgressBarProps extends Omit<LinearProgressProps, 'value' | 'variant' | 'color'> {
    confirmedTags?: number;
    totalTags?: number;
    showPercentage?: boolean;
    showCount?: boolean;
    animated?: boolean;
    colorMode?: 'primary' | 'success' | 'warning';
    sx?: SxProps<Theme>;
}

const progressAnimation = keyframes`
    0% {
        background-position: 160% 0;
    }

    100% {
        background-position: -60% 0;
    }
`;

const getProgressValue = (confirmedTags: number, totalTags: number) => {
    if (totalTags <= 0) {
        return 0;
    }

    const progress = Math.round((confirmedTags / totalTags) * 100);

    return Math.min(Math.max(progress, 0), 100);
};

const getProgressColor = ({
    theme,
    progress,
    colorMode,
}: {
    theme: Theme;
    progress: number;
    colorMode: 'primary' | 'success' | 'warning';
}) => {
    if (progress >= 100) {
        return theme.palette.success.main;
    }

    return theme.palette[colorMode].main;
};

const TagProgressBar = ({
    confirmedTags = 0,
    totalTags = 0,
    showPercentage = false,
    showCount = false,
    animated = true,
    colorMode = 'primary',
    sx,
    ...props
}: TagProgressBarProps) => {
    const progress = getProgressValue(confirmedTags, totalTags);
    const isCompleted = progress >= 100;
    const shouldAnimate = animated && !isCompleted && progress > 0;

    return (
        <Box sx={sx}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <LinearProgress
                        {...props}
                        sx={getLinearProgressSx({
                            progress,
                            colorMode,
                            shouldAnimate,
                        })}
                        variant="determinate"
                        value={progress}
                        aria-label="Tag registration progress"
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    />
                </Box>

                {(showPercentage || showCount) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexShrink: 0,
                        }}
                    >
                        {showPercentage && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: isCompleted ? 'success.main' : 'text.secondary',
                                    fontWeight: isCompleted ? 600 : 400,
                                }}
                            >
                                {progress}%
                            </Typography>
                        )}

                        {showCount && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                ( {confirmedTags} / {totalTags} )
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const getLinearProgressSx = ({
    progress,
    colorMode,
    shouldAnimate,
}: {
    progress: number;
    colorMode: 'primary' | 'success' | 'warning';
    shouldAnimate: boolean;
}): SxProps<Theme> => ({
    height: 10,
    borderRadius: 999,

    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: (theme) => {
            const barColor = getProgressColor({
                theme,
                progress,
                colorMode,
            });

            return alpha(barColor, 0.14);
        },
    },

    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 999,
        backgroundColor: (theme) =>
            getProgressColor({
                theme,
                progress,
                colorMode,
            }),
        backgroundImage: shouldAnimate
            ? (theme) => {
                  const barColor = getProgressColor({
                      theme,
                      progress,
                      colorMode,
                  });

                  return `linear-gradient(
                      90deg,
                      ${barColor} 0%,
                      ${alpha(theme.palette.common.white, 0.28)} 45%,
                      ${barColor} 90%
                  )`;
              }
            : 'none',
        backgroundSize: '180% 100%',
        backgroundRepeat: 'no-repeat',
        animation: shouldAnimate ? `${progressAnimation} 2.4s ease-in-out infinite` : 'none',
        transition: 'transform 0.45s ease, background-color 0.25s ease',
    },

    '@media (prefers-reduced-motion: reduce)': {
        [`& .${linearProgressClasses.bar}`]: {
            animation: 'none',
        },
    },
});

export default TagProgressBar;
